'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter, useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { allAccountsSuspended } from '@/lib/auth/account-status';
import { safeNext } from '@/lib/auth/safe-next';
import { createClient } from '@/lib/supabase/client';
import { loginSchema, type LoginInput } from '@/lib/validation/auth';

export default function LoginForm() {
  const router = useRouter();
  const sp = useSearchParams();
  // Never hand a raw query param to router.replace — `javascript:` targets
  // execute in the page context once sign-in succeeds.
  const next = safeNext(sp.get('next'), '/');
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({ resolver: zodResolver(loginSchema) });

  async function onSubmit(values: LoginInput) {
    setServerError(null);
    const supabase = createClient();
    const { data: signInData, error } = await supabase.auth.signInWithPassword(values);
    if (error) {
      setServerError(error.message);
      return;
    }
    // Block sign-in if the profile is suspended/pending. Supabase Auth allows
    // login regardless of profiles.status, so we enforce status here on the
    // friendly UI side. The proxy + requireRole are belt + braces server-side.
    const userId = signInData.user?.id;
    if (userId) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('status, role')
        .eq('id', userId)
        .maybeSingle();
      if (profile?.status && profile.status !== 'active') {
        await supabase.auth.signOut();
        setServerError(
          profile.status === 'suspended'
            ? 'Your account has been locked. Contact your sales rep at Link Imaging to restore access.'
            : 'Your account is pending approval. You will be notified once an admin activates it.',
        );
        return;
      }
      // Account-level check: an active USER whose every dealer account is
      // suspended gets the same treatment. RLS hides suspended accounts from
      // members, so a suspended account shows up as a null `accounts` join
      // (see allAccountsSuspended). The proxy enforces this server-side too.
      if (profile?.role === 'dealer') {
        const { data: memberships } = await supabase
          .from('account_members')
          .select('account_id, accounts(status)')
          .eq('user_id', userId);
        if (allAccountsSuspended(memberships)) {
          await supabase.auth.signOut();
          setServerError(
            'Your company’s account has been suspended. Contact your sales rep at Link Imaging to restore access.',
          );
          return;
        }
      }
    }
    router.replace(next);
    router.refresh();
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
      <div>
        <Label htmlFor="email">Email</Label>
        <Input id="email" type="email" autoComplete="email" {...register('email')} />
        {errors.email && <p className="text-xs text-red-600">{errors.email.message}</p>}
      </div>
      <div>
        <Label htmlFor="password">Password</Label>
        <PasswordInput id="password" autoComplete="current-password" {...register('password')} />
        {errors.password && <p className="text-xs text-red-600">{errors.password.message}</p>}
      </div>
      {serverError && <p className="text-sm text-red-600">{serverError}</p>}
      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? 'Signing in…' : 'Sign in'}
      </Button>
      <div className="flex justify-between text-xs">
        <a href="/forgot-password" className="text-neutral-600 underline">
          Forgot password?
        </a>
        <a href="/register" className="text-neutral-600 underline">
          Register
        </a>
      </div>
    </form>
  );
}
